import centroid from '@turf/centroid';
import moment from 'moment';
import { defaultEnvironment } from '../environment';
import { Event } from '../models';
import { AppService } from './app';
import { backendTypesMap, parseChallengeTemplate, parseGeogroup, subGroupTypesMap, typesMap } from './geogroup';
import { HttpService } from './http';
export class EventService {
  static async getAvailableEvents({
    partner,
    geogroupId,
    userLocation,
    page,
    pageSize,
    state,
    automaticSubscription,
    excludeJoinedEvents,
    bannerEnabled,
    onlyPublic,
    ordering
  }) {
    try {
      const params = [{
        key: 'page',
        value: page
      }, {
        key: 'page_size',
        value: pageSize
      }];
      if (state) state.forEach(value => params.push({
        key: 'state[]',
        value
      }));
      if (automaticSubscription !== undefined) params.push({
        key: 'automatic_subscription',
        value: automaticSubscription
      });
      if (userLocation) {
        params.push({
          key: 'user_lat',
          value: userLocation.coordinates[1]
        }, {
          key: 'user_long',
          value: userLocation.coordinates[0]
        });
      }
      if (excludeJoinedEvents !== undefined) {
        params.push({
          key: 'exclude_joined_events',
          value: excludeJoinedEvents
        });
      }
      if (bannerEnabled !== undefined) params.push({
        key: 'banner_enabled',
        value: bannerEnabled
      });
      if (onlyPublic !== undefined) params.push({
        key: 'public',
        value: onlyPublic
      });
      if (ordering !== undefined) params.push({
        key: 'ordering',
        value: ordering
      });
      params.push({
        key: 'target_type',
        value: 'BTTF_POINTS,CUSTOM,CYCLING_DAYS,TRACES_COUNT,TRAVELED_DISTANCE'
      });
      const {
        results
      } = await HttpService.get('v2', `/geogroups/${geogroupId}/available_events`, params, [], null, partner ? {
        partner: partner.code
      } : {});
      return results.map(data => parseEvent(data, AppService.environment.backendUrl));
    } catch (err) {
      console.error('[EventService][getAvailableEvents]', err);
      throw err;
    }
  }
  static async getEvents({
    period,
    search,
    isPublic,
    ordering,
    page,
    pageSize,
    query
  }) {
    try {
      const params = [{
        key: 'state[]',
        value: period === 'current' ? 'running' : period === 'future' ? 'future' : 'finished'
      }, {
        key: 'ordering',
        value: ordering
      }, {
        key: 'page',
        value: page
      }, {
        key: 'page_size',
        value: pageSize
      }, {
        key: 'query',
        value: query
      }];
      if (search) params.push({
        key: 'search',
        value: search
      });
      if (isPublic !== undefined) params.push({
        key: 'public',
        value: isPublic
      });
      const {
        count,
        results
      } = await HttpService.get('v3', `/events`, params);
      const events = results.map(data => parseEvent(data, AppService.environment.backendUrl));
      return {
        count,
        events
      };
    } catch (err) {
      console.error('[EventService][getEvents]', err);
      throw err;
    }
  }
  static async getEvent(id) {
    try {
      const data = await HttpService.get('v3', `/events/${id}`);
      return parseEvent(data, AppService.environment.backendUrl);
    } catch (err) {
      console.error('[EventService][getEvent]', err);
      throw err;
    }
  }
  static async getPartnerEvents(partnerId) {
    try {
      const {
        results
      } = await HttpService.get('v3', `/partners/${partnerId}/events`);
      return results.map(data => parseEvent(data, AppService.environment.backendUrl));
    } catch (err) {
      console.error('[EventService][getPartnerEvents]', err);
      throw err;
    }
  }
  static async getRanking(eventId, {
    page,
    pageSize,
    groupsIds,
    groupTypes,
    size,
    cyclabilityZoneId,
    areaId,
    search,
    parentId,
    holdingGroupId,
    showSites
  }) {
    try {
      const queryParams = [{
        key: 'page',
        value: page
      }, {
        key: 'page_size',
        value: pageSize
      }];
      if (groupsIds && groupsIds.length > 0) queryParams.push(...groupsIds.map(id => ({
        key: 'group_ids[]',
        value: id
      })));
      if (groupTypes.length > 0) queryParams.push({
        key: 'geogroup_type',
        value: groupTypes.map(key => typesMap[key]).join(',')
      });
      if (size?.min) queryParams.push({
        key: 'size_range_min',
        value: size.min
      });
      if (size?.max) queryParams.push({
        key: 'size_range_max',
        value: size.max
      });
      if (cyclabilityZoneId) queryParams.push({
        key: 'in_cyclability_zone',
        value: cyclabilityZoneId
      });
      if (areaId) queryParams.push({
        key: 'in_area',
        value: areaId
      });
      if (search) queryParams.push({
        key: 'search',
        value: search
      });
      if (parentId) queryParams.push({
        key: 'parent_id',
        value: parentId
      });
      if (holdingGroupId) queryParams.push({
        key: 'holding_group_id',
        value: holdingGroupId
      });
      if (showSites !== undefined) queryParams.push({
        key: 'show_sites',
        value: showSites
      });
      const {
        count,
        results
      } = await HttpService.get('v5', `/events/${eventId}/ranking`, queryParams);
      return {
        count,
        items: results.map(data => parseEventRankingItem(data, AppService.environment.backendUrl))
      };
    } catch (err) {
      console.error('[EventService][getRanking]', err);
      throw err;
    }
  }
  static async getIndividualRanking(eventId, {
    page,
    pageSize,
    ...props
  }) {
    try {
      const queryParams = [{
        key: 'page',
        value: page
      }, {
        key: 'page_size',
        value: pageSize
      }];
      if ('search' in props && props.search) queryParams.push({
        key: 'search',
        value: props.search
      });
      if ('getConnectedUserRank' in props && props.getConnectedUserRank) queryParams.push({
        key: 'connected_user_rank',
        value: true
      });
      const {
        count,
        results
      } = await HttpService.get('v3', `/events/${eventId}/user_ranking`, queryParams);
      return {
        count,
        items: results.map(data => parseEventIndividualRankingItem(data, AppService.environment.backendUrl))
      };
    } catch (err) {
      console.error('[EventService][getIndividualRanking]', err);
      throw err;
    }
  }
  static async getCyclabilityZones(eventId) {
    try {
      const res = await HttpService.get('v2', `/events/${eventId}/cyclability_zones`);
      return res.map(({
        region_id: regionId,
        epcis
      }) => ({
        regionId,
        children: epcis.map(({
          epci_id: id,
          epci_name: name
        }) => ({
          id,
          name
        }))
      }));
    } catch (err) {
      console.error('[EventService][getCyclabilityZones]', err);
      throw err;
    }
  }
  static async addGeogroup(eventId, {
    type,
    name,
    min,
    max,
    address,
    cyclabilityZone,
    icon,
    bannerPicture,
    description,
    linkUrl,
    linkLabel
  }) {
    try {
      let data = {
        type: typesMap[type],
        title: name,
        code: name.trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/\s+/g, '_'),
        size_range: JSON.stringify({
          min: min || 0,
          max: max || 100_000_000
        })
      };
      if (description !== undefined) data = {
        ...data,
        description
      };
      if (address) data = {
        ...data,
        geo_point: JSON.stringify(address.point),
        address: address.address
      };else if (cyclabilityZone) {
        const {
          geometry
        } = cyclabilityZone;
        if (!geometry) throw new Error('cyclability zone has no geometry');
        data = {
          ...data,
          geo_point: JSON.stringify(centroid(geometry).geometry),
          cyclability_zone: JSON.stringify(cyclabilityZone.id)
        };
      }
      if (linkUrl !== undefined) {
        data = {
          ...data,
          link_url: linkUrl,
          link_label: linkLabel || ''
        };
      }
      const formData = new FormData();
      Object.keys(data).forEach(key => formData.append(key, data[key]));
      if (icon) formData.append('photo', icon);
      if (bannerPicture) formData.append('banner_picture', bannerPicture);
      const props = await HttpService.post('v3', `/events/${eventId}/geogroups`, [], [], formData);
      return parseGeogroup(props, AppService.environment.backendUrl);
    } catch (err) {
      console.error('[EventService][addGeogroup]', err);
      throw err;
    }
  }
  static async getRegistrableGeogroups(userId, eventId) {
    try {
      const res = await HttpService.get('v5', `/users/${userId}/events/${eventId}/can_participate_groups`);
      return res.map(data => parseGeogroup(data, AppService.environment.backendUrl));
    } catch (err) {
      console.error('[EventService][getRegistrableGeogroups]', err);
      throw err;
    }
  }
  static async getEventCoverage(userId, eventId, geoGroupId) {
    try {
      const res = await HttpService.get('v5', `/users/${userId}/events/${eventId}/can_participate_groups`, [{
        key: 'query',
        value: '{id, covered_by_paid_contract}'
      }, {
        key: 'exclude_joined_events',
        value: false
      }]);
      return res.find(({
        id
      }) => id === geoGroupId)?.covered_by_paid_contract ?? false;
    } catch (err) {
      console.error('[EventService][getEventCoverage]', err);
      throw err;
    }
  }
  static async getOffers(eventId) {
    try {
      const results = await HttpService.get('v2', `/events/${eventId}/offers`);
      return results.map(parseEventOffer);
    } catch (err) {
      console.error('[EventService][getOffers]', err);
      throw err;
    }
  }
}
function parseEventOffer({
  id,
  title,
  price_ht,
  payment_price_id,
  early_bird_price_ht,
  early_bird_payment_price_id,
  sales_appointment_url,
  webinar_url,
  early_bird_end_datetime,
  max_company_workforce,
  free_participation_allowed
}) {
  return {
    id,
    title,
    priceHt: price_ht,
    paymentPriceId: payment_price_id,
    earlyBirdPriceHt: early_bird_price_ht,
    earlyBirdPaymentPriceId: early_bird_payment_price_id,
    salesAppointmentUrl: sales_appointment_url,
    webinarUrl: webinar_url,
    earlyBirdEndDatetime: early_bird_end_datetime,
    maxCompanyWorkforce: max_company_workforce,
    freeParticipationAllowed: free_participation_allowed
  };
}
function parseEvent({
  id,
  title,
  code,
  start_datetime,
  end_datetime,
  challenge_template,
  scope_permited_contract_group_ids,
  scope_permited_contract_codes,
  scope_permited_group_types,
  scope_permited_sub_group_type,
  scope_permited_group_geo_polygon,
  challenges_count,
  challenges_progress_value,
  public: _public,
  dedicated_website_url,
  sponsor,
  subtitle,
  banner_primary_color,
  banner_secondary_color,
  text_primary_color,
  text_secondary_color,
  communication_kit,
  default_participation_group,
  icon,
  target_levels,
  enable_individual_leaderboard,
  allow_company_site_filter,
  allow_geographic_filter,
  h3_game
}, backendUrl) {
  const scopePermitedContractGroupIds = scope_permited_contract_group_ids || [];
  const scopePermitedContractCodes = scope_permited_contract_codes || [];
  const scopePermitedGroupTypes = scope_permited_group_types || [];
  const scopePermitedSubGroupType = scope_permited_sub_group_type;
  let allowedParticipantsTypes = [];
  if (scopePermitedSubGroupType) {
    allowedParticipantsTypes = ['COMPANY'];
  } else if (scopePermitedContractGroupIds.length === 0 && scopePermitedContractCodes.length === 0 && scopePermitedGroupTypes.length === 0) {
    allowedParticipantsTypes = ['ASSOCIATION', 'CITY', 'COMPANY', 'PRIVATE', 'SCHOOL'];
  } else {
    allowedParticipantsTypes = scopePermitedGroupTypes;
  }
  return new Event(id, title, code || null, moment(start_datetime), moment(end_datetime), challenge_template ? parseChallengeTemplate(id, {
    ...challenge_template,
    title,
    start_datetime,
    end_datetime
  }, backendUrl) : null, scopePermitedContractCodes, allowedParticipantsTypes.map(key => backendTypesMap[key]), scope_permited_sub_group_type && subGroupTypesMap[scope_permited_sub_group_type] || null, scope_permited_group_geo_polygon || null, challenges_count || 0, challenges_progress_value || 0, _public ? true : false, dedicated_website_url || null, sponsor || null, subtitle || null, banner_primary_color || null, banner_secondary_color || null, text_primary_color || null, text_secondary_color || null, communication_kit || null, default_participation_group || null, icon ? icon.indexOf('https') === -1 && icon.indexOf('http') === -1 ? `${backendUrl}${icon}` : icon : null, enable_individual_leaderboard, allow_company_site_filter, allow_geographic_filter, target_levels ? target_levels.map(({
    title,
    description,
    picture,
    target: targetValue
  }) => ({
    title,
    picture: `${defaultEnvironment.backendUrl}${picture}`,
    targetValue,
    description
  })) : null, h3_game ?? null);
}
function parseEventRankingItem({
  id,
  title,
  type,
  photo,
  parent_id: parentId,
  parent_title: parentTitle,
  parent_photo,
  rank,
  leaderboard_index: leaderboardIndex,
  progress_value: progressValue,
  count_participants: participantsCount,
  count_participants_with_not_null_progress,
  size_range,
  challenge_certified: certified,
  challenge_id
}, backendUrl) {
  return {
    id,
    title,
    type: backendTypesMap[type],
    photo: photo ? `${backendUrl}${photo}` : null,
    parentId,
    parentTitle,
    parentPhoto: parent_photo ? `${backendUrl}${parent_photo}` : null,
    rank,
    leaderboardIndex,
    progressValue,
    participantsCount,
    activeParticipantsCount: count_participants_with_not_null_progress || 0,
    sizeRange: size_range || null,
    certified,
    challengeId: challenge_id || null
  };
}
function parseEventIndividualRankingItem({
  user,
  group,
  rank,
  leaderboard_index: leaderboardIndex,
  progress_value: progressValue
}, backendUrl) {
  return {
    user: {
      id: user.id,
      username: user.username || '',
      profilePicture: user.profile_picture ? `${backendUrl}${user.profile_picture}` : null
    },
    group: {
      id: group.id,
      type: backendTypesMap[group.type],
      title: group.title || '',
      photo: group.photo ? `${backendUrl}${group.photo}` : null,
      sizeRange: group.size_range || null,
      parent: group.parent ? {
        id: group.parent.id,
        title: group.parent.title,
        photo: group.parent.photo,
        sizeRange: group.parent.size_range || null
      } : null
    },
    rank,
    leaderboardIndex,
    progressValue
  };
}